// Pure policy for the client diagnostics beacon (specs/observability): code
// generation, event buffering, and flush limits. No DOM, no Convex — the
// runtime wiring lives in telemetry.ts. Everything here is unit-tested.

export interface TelemetryEvent {
  kind: 'error' | 'rejection' | 'console' | 'crumb';
  message: string;
  stack?: string;
  at: number;
}

/** Same no-ambiguity idea as invite codes: no I/L/O/0/1. */
export const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const CODE_LENGTH = 4;

/** Crumb history kept in memory and resent as context with every batch. */
export const CRUMB_CAP = 40;

/** A session flushes at most this many batches — the runaway ceiling. */
export const MAX_FLUSHES_PER_SESSION = 8;

/** Errors burst; collect for a beat and flush once. */
export const FLUSH_DEBOUNCE_MS = 1500;

/** One short code per page load — four glyphs a non-technical user can read aloud. */
export function generateCode(random: () => number = Math.random): string {
  let code = '';
  for (let i = 0; i < CODE_LENGTH; i++) {
    const index = Math.min(Math.floor(random() * CODE_ALPHABET.length), CODE_ALPHABET.length - 1);
    code += CODE_ALPHABET[index];
  }
  return code;
}

/** Normalize whatever was thrown into message + optional stack. */
export function describeThrown(value: unknown): { message: string; stack?: string } {
  if (value instanceof Error) {
    return { message: `${value.name}: ${value.message}`, stack: value.stack };
  }
  if (typeof value === 'string') return { message: value };
  if (value === null || value === undefined) return { message: String(value) };
  try {
    return { message: JSON.stringify(value) };
  } catch {
    return { message: Object.prototype.toString.call(value) };
  }
}

/**
 * Append an event, dropping the oldest past `cap`. Consecutive duplicates
 * (same kind + message) refresh the timestamp instead of stacking — reactive
 * statements re-fire with unchanged values and must not flood the buffer.
 */
export function pushEvent(events: TelemetryEvent[], event: TelemetryEvent, cap = CRUMB_CAP): void {
  const last = events[events.length - 1];
  if (last && last.kind === event.kind && last.message === event.message) {
    last.at = event.at;
    return;
  }
  events.push(event);
  if (events.length > cap) events.splice(0, events.length - cap);
}

export function canFlush(flushCount: number): boolean {
  return flushCount < MAX_FLUSHES_PER_SESSION;
}

/** Crumb context + the captured errors, oldest-first, for one report batch. */
export function buildReportEvents(
  crumbs: TelemetryEvent[],
  captured: TelemetryEvent[]
): TelemetryEvent[] {
  return [...crumbs, ...captured].sort((a, b) => a.at - b.at);
}
